import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, Inject, forwardRef } from '@angular/core';
import { SharedModule } from "@Shared/shared.module";
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@App';
import * as _ from 'lodash';
import { UserSelectionTableComponent } from '../users-selection-table/users-selection-table.component';
import { SubSink } from 'subsink';
import { UserAdminService, TeamsService } from '@Services'
import { teamRequest } from '@Models';
import { MonitoringService } from '@App';
import { TeamUserRole } from '@Enums';

@Component({
    selector: "create-team",
    templateUrl: "./create-team.component.html",
    styleUrls: ["./create-team.component.scss"],
    imports: [SharedModule, MatStepperModule, UserSelectionTableComponent]
})
export class CreateTeamComponent implements OnInit, OnDestroy {
    @ViewChild(MatStepper) stepper: MatStepper;
    @Input() teams: any[];
    @Output() changesSaved = new EventEmitter<boolean>();

    clients: any[] = [];
    allTeams: any[] = [];
    teamForm: UntypedFormGroup;
    currentUserId: any;
    currentUserName: any;
    currentUserRoleName: any;

    private readonly subs = new SubSink();

    adminUserList: any[] = [];
    teamMemberList: any[] = [];

    allPossibleTeamMembers: any[] = [];

    constructor(
        private _fb: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<CreateTeamComponent>,
        private appService: AppService,
        private readonly userAdminService: UserAdminService,
        private monitoringService: MonitoringService,
        private readonly teamsService: TeamsService
    ) {
        this.teams = data.teams;
    }

    ngOnInit() {

        this.getCurrentUser();
        this.teamForm = this._fb.group({
            clientId: ['', Validators.required],
            teamName: [{ value: '', disabled: true }, [
                Validators.required,
                Validators.maxLength(50),
                this.noWhitespaceValidator,
                this.duplicateTeamNameValidator.bind(this)]],
            adminUsers: [[], this.atLeastOneSelectedValidator],
            teamMembers: [[], this.atLeastOneSelectedValidator]
        });

        this.getClients();
        this.teamForm.get('clientId').valueChanges.subscribe(clientId => {
            const teamNameControl = this.teamForm.get('teamName');
            if (clientId) {
                teamNameControl.enable();
            }
            else {
                teamNameControl.disable();
                teamNameControl.disable();
            }
        });

        this.getEligibleUsersForClient();
        this.getAllTeamsForDuplicateNameCheck();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    getClients() {
        this.appService.getData('Clients').subscribe(
            response => {
                if (response.data && response.data.length) {
                    this.clients = _.sortBy(response.data, [function (o) { return o.name; }]);
                    if (this.clients && this.clients.length === 1) {
                        this.teamForm.get('clientId').setValue(this.clients[0].id)
                    }
                }
            }
        );
    }

    getCurrentUser() {
        this.currentUserId = this.appService.getUserId();
        this.currentUserName = this.appService.getUsername();
        this.currentUserRoleName = this.appService.getUserRoleName();
    }

    getEligibleUsersForClient() {
        this.teamForm.get('clientId').valueChanges.subscribe(clientId => {
            if (clientId) {
                this.subs.add(this.teamsService.getAdminUsersForClient(clientId).subscribe(users => {
                    const selectedAdminIds = new Set(this.teamForm.get('adminUsers').value.map(u => u.id));

                    // Preselecting the user who created the team as one of the team admins.
                    selectedAdminIds.add(this.currentUserId);

                    this.adminUserList = users.map(u => ({
                        id: u.id,
                        name: u.name,
                        role: u.userRoleName,
                        selected: selectedAdminIds.has(u.id)
                    }));

                    if (this.adminUserList.some(a => a.id !== this.currentUserId)) {
                        this.monitoringService.logEvent('Teams: Current user not found in admin list. Please check user access and role permissions.');
                    }

                    this.teamForm.get('adminUsers').setValue(
                        this.adminUserList.filter(u => u.selected)
                    );

                    const currentUserAdmin = this.adminUserList.find(a => a.id === this.currentUserId);
                    const otherAdmins = this.adminUserList.filter(a => a.id !== this.currentUserId).sort((a, b) => a.name.localeCompare(b.name));

                    this.adminUserList = currentUserAdmin ? [currentUserAdmin, ...otherAdmins] : otherAdmins;
                    this.teamForm.get('adminUsers').updateValueAndValidity();
                }));

                this.subs.add(this.teamsService.getUsersForClient(clientId).subscribe(users => {
                    this.teamMemberList = users
                        .map(u => ({
                            id: u.id,
                            name: u.name,
                            role: u.userRoleName,
                            selected: false
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    this.teamForm.get('teamMembers').setValue([]);
                    this.teamForm.get('teamMembers').updateValueAndValidity();
                }));
            }
        });
    }

    getAllTeamsForDuplicateNameCheck() {
        this.subs.add(this.userAdminService.getAllTeams(true).subscribe(data => {
            this.allTeams = data.map((team) => ({
                ...team,
                id: team.id,
                name: team.name,
                clientId: team.clientId
            }));
        }));
    }

    onStep2Next() {
        this.stepper.next();
    }

    onAdminSelectionChange(selectedAdmins) {
    this.teamForm.patchValue({ adminUsers: selectedAdmins });
    this.teamForm.get('adminUsers').updateValueAndValidity();

    }

    onTeamMemberSelectionChange(selectedUsers) {
        this.teamForm.patchValue({ teamMembers: selectedUsers });
        this.teamForm.get('teamMembers').updateValueAndValidity();

    }

    cancel() {
        this.dialogRef.close();
    }

    saveAndClose() {
        if (this.teamForm.valid) {
            const adminMembers = this.teamForm.get('adminUsers').value;
            const teamMembers = this.teamForm.get('teamMembers').value;

            const userMap = new Map<string, { userId: string, role: TeamUserRole }>();

            adminMembers.forEach(u => {
                userMap.set(u.id, { userId: u.id, role: TeamUserRole.AdminOnly });
            });

            teamMembers.forEach(u => {
                if (userMap.has(u.id)) {
                    userMap.set(u.id, { userId: u.id, role: TeamUserRole.AdminAndMember });
                } else {
                    userMap.set(u.id, { userId: u.id, role: TeamUserRole.MemberOnly });
                }
            });
            
            const users = Array.from(userMap.values());

            let request: teamRequest = {
                name: this.teamForm.get('teamName').value,
                clientId: this.teamForm.get('clientId').value,
                teamMembers: users
            };

            this.subs.add(this.teamsService.createTeam(request).subscribe(result => {
                if (result) {
                    this.changesSaved.emit(true);
                    this.dialogRef.close(this.teamForm.value);
                }
            }));
        }
    }

    duplicateTeamNameValidator(control) {
        if (!control.value) {
            return null;
        }

        const clientId = this.teamForm.get('clientId').value;
        if (clientId) {
            const name = control.value.trim().toLowerCase();
            const isDuplicate = this.allTeams
                .filter(team => team.clientId === clientId)
                .some(team => team.name.toLowerCase() === name);

            return isDuplicate ? { duplicate: true } : null;
        }
        return null;
    }

    noWhitespaceValidator(control) {
        const isWhitespace = (control.value || '').trim().length == 0;
        return isWhitespace ? { whiteSpace: true } : null;
    }

    atLeastOneSelectedValidator(control) {
        return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
    }
}